<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';
import { useDisplay } from 'vuetify';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import useLocale from './helpers/use-locale';
import SchoolUnitSelect from './SchoolUnitSelect.vue';
import UserSelectionTable from './UserSelectionTable.vue';
import GroupSelectionTable from './GroupSelectionTable.vue';

type Emits = {
  (e: 'update:user', selectedIds: string[]): void;
  (e: 'update:group', selectedIds: string[]): void;
};
const emit = defineEmits<Emits>();
type Props = {
  preSelectedUserIds: string[];
  preSelectedGroupIds: string[];
  canShareWithGroup?: boolean;
  userId?: string;
  userType?: 'employee' | 'student';
  forceSmaller?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  canShareWithGroup: true,
  userId: undefined,
  userType: undefined,
  forceSmaller: undefined,
});
const { t } = useLocale();
const { smAndDown, lgAndUp } = useDisplay();
const { preSelectedUserIds, preSelectedGroupIds, canShareWithGroup, userId, userType, forceSmaller } = toRefs(props);
const tab = ref<number>(1);
const selectedSchoolUnit = ref<SlimOrganization | undefined>(undefined);
const isFetchingSchoolUnits = ref<boolean>(false);
const isSmAndDown = computed(() => forceSmaller.value ?? smAndDown.value);
</script>

<template>
  <v-card-title class="px-0 justify-space-between align-center" :class="isSmAndDown ? '' : 'd-flex'">
    <v-tabs v-model="tab" :class="isSmAndDown ? 'tabs-mobile' : 'tabs'" grow>
      <v-tab
        color="on-surface"
        class="text-none"
        :class="isSmAndDown ? 'tab-mobile' : 'tab'"
        prepend-icon="mdi-account"
        :value="1"
        >{{ t('components.users') }}
      </v-tab>
      <v-tab
        v-if="canShareWithGroup"
        color="on-surface"
        class="text-none"
        :class="isSmAndDown ? 'tab-mobile' : 'tab'"
        prepend-icon="mdi-account-multiple"
        :value="2"
      >
        {{ t('components.groups') }}
      </v-tab>
    </v-tabs>
    <div class="d-flex">
      <SchoolUnitSelect
        v-model="selectedSchoolUnit"
        class="ma-1"
        :width="lgAndUp ? '450px' : '400px'"
        @isLoading="(isLoading: boolean) => (isFetchingSchoolUnits = isLoading)"
      />
    </div>
  </v-card-title>
  <v-window v-model="tab" :style="isSmAndDown ? 'overflow-y: scroll' : 'overflow: auto'" class="mx-n4">
    <v-window-item :value="1" :transition="false" :reverse-transition="false">
      <UserSelectionTable
        v-if="selectedSchoolUnit"
        :model-value="preSelectedUserIds"
        :is-fetching-school-units="isFetchingSchoolUnits"
        :school-unit-id="selectedSchoolUnit.uid"
        :user-id="userId"
        :user-type="userType"
        :force-smaller="isSmAndDown"
        @update:modelValue="emit('update:user', $event)"
      />
    </v-window-item>
    <v-window-item v-if="canShareWithGroup" :value="2" :transition="false" :reverse-transition="false">
      <GroupSelectionTable
        v-if="selectedSchoolUnit"
        :model-value="preSelectedGroupIds"
        :is-fetching-school-units="isFetchingSchoolUnits"
        :school-unit-id="selectedSchoolUnit.uid"
        :force-smaller="isSmAndDown"
        @update:modelValue="emit('update:group', $event)"
      />
    </v-window-item>
  </v-window>
</template>

<style scoped lang="scss">
.v-tabs {
  background-color: rgb(var(--v-theme-surface-container-high));
  width: 50%;
  border-radius: 10px;
}
.tabs-mobile {
  margin: auto;
  width: 100%;
}
.tabs {
  align-content: space-around;
}
.tab {
  width: 50%;
}
.tab-mobile {
  width: 50%;
}
.v-tab--selected {
  background-color: rgb(var(--v-theme-on-surface));
  color: rgb(var(--v-theme-surface));
}
.users-selected-mobile {
  font-size: small;
  position: fixed;
  bottom: 0px;
  width: 100%;
  right: 0px;
}
.users-selected {
  position: fixed;
  width: 100%;
  bottom: 0px;
  right: 0px;
}
</style>
